import { useMemo, useState } from "react";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ActorCard from "@/components/feature/ActorCard";
import { actors } from "@/mocks/actors";

const roleTabs = ["全部", "主角", "配角"];

export default function ActorsCategory() {
  const [keyword, setKeyword] = useState("");
  const [roleType, setRoleType] = useState("全部");
  const [sortAsc, setSortAsc] = useState(true);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = actors.filter((actor) => {
      const matchRole = roleType === "全部" || actor.roleType === roleType;
      const matchKeyword =
        !q || actor.name.toLowerCase().includes(q) || actor.role.toLowerCase().includes(q);
      return matchRole && matchKeyword;
    });
    return list.sort((a, b) =>
      sortAsc ? a.initial.localeCompare(b.initial) : b.initial.localeCompare(a.initial),
    );
  }, [keyword, roleType, sortAsc]);

  const resetFilters = () => {
    setKeyword("");
    setRoleType("全部");
    setSortAsc(true);
  };

  return (
    <>
      <PageMeta
        title="年轻的母亲1演员介绍 - 年轻的母亲1官网"
        description="年轻的母亲1演员介绍：查看主要出演演员的资料、照片与所饰角色，全面了解演员阵容与人物关系。"
        keywords="年轻的母亲1演员,年轻的母亲1演员表,年轻的母亲1角色介绍"
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "演员" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">演员介绍</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            介绍《年轻的母亲1》的主要出演演员。你可以通过角色类型分组或姓名搜索快速找到想了解的演员，点击卡片进入演员详情页查看完整资料与参演剧集。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
        <div className="mb-7 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex max-w-xs flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索演员姓名或角色名"
              className="h-6 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword ? (
              <button
                type="button"
                onClick={() => setKeyword("")}
                aria-label="清空搜索"
                className="text-foreground-400 transition-colors hover:text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {roleTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setRoleType(tab)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    roleType === tab
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSortAsc((prev) => !prev)}
              className="flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600 transition-colors hover:bg-secondary-100"
            >
              <i className={sortAsc ? "ri-sort-asc" : "ri-sort-desc"} />
              姓名首字母{sortAsc ? "升序" : "降序"}
            </button>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((actor) => (
              <ActorCard key={actor.id} actor={actor} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="没有找到匹配的演员"
            description="换个关键词或角色类型试试，也可以一键重置筛选条件。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        )}
      </div>
    </>
  );
}